import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Link,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';
import SearchIcon from '@mui/icons-material/Search';
import StorefrontIcon from '@mui/icons-material/Storefront';
import InventoryIcon from '@mui/icons-material/Inventory2';
import type {
  CatalogProduct,
  ProductsListItem,
  QuoteItem,
  QuoteOption,
} from '@techgitter/contracts';
import { QuoteOptionSearchDialog } from './QuoteOptionSearchDialog';
import { DistiListingSearchDialog } from './DistiListingSearchDialog';
import { AddRetailOptionDialog } from './AddRetailOptionDialog';
import { InventoryPickerDialog } from './InventoryPickerDialog';
import { PriceInput } from './PriceInput';
import { BillingGroupSelect, BillingGroupsPanel } from './BillingGroupControls';
import { SearchAhead } from '../dropdown';
import { useProductCatalog } from '../../hooks/useProductCatalog';
import {
  formatSpecSummary,
  specDiffersFromDescription,
} from '../../utils/quoteOptionSpecs';
import { cleanSupplierUrl } from '../../utils/cleanSupplierUrl';
import { optionQty, optionLineSellDisplay } from '../../utils/quotePricing';
import {
  DESKTOP_BUILD_CATEGORIES,
  CUSTOM_CATEGORY_PREFIX,
  desktopBuildCategoryLabel,
  desktopBuildCategoryAllowsMultiple,
  desktopBuildDefaultProductCode,
  isCustomCategorySlug,
} from './desktopBuildCategories';
import { computeDesktopBuildProfit } from '../../utils/desktopBuildProfit';
import { DesktopBuildProfitSummary } from './DesktopBuildProfitSummary';
import './DesktopBuildEditor.css';

const newId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const blankOption = (): QuoteOption => ({
  id: newId(),
  description: '',
  buyPriceExGst: 0,
  shipping: 0,
  url: '',
  sellPrice: 0,
  productCode: '',
  quantity: 1,
  note: '',
});

/**
 * An empty-category placeholder item. Carries the category slug + label but no
 * options, so the category persists on the quote (survives a reload) while
 * being invisible to promotion / totals / preview (all of which skip unmarked,
 * option-less items). Removing a category deletes its placeholder (+ any parts).
 */
const categoryPlaceholder = (
  slug: string,
  label: string,
  position: number,
): QuoteItem => ({
  id: newId(),
  name: label,
  category: slug,
  position,
  selectedOptionId: null,
  markedForInvoice: false,
  options: [],
});

/** Build a QuoteOption from an inventory catalog pick (unitCost is inc-GST). */
const optionFromInventory = (product: ProductsListItem): QuoteOption => {
  const exGstCost = Math.round(((product.unitCost ?? 0) / 1.1) * 100) / 100;
  return {
    id: newId(),
    description: product.displayName || product.sku,
    buyPriceExGst: exGstCost,
    shipping: 0,
    url: '',
    sellPrice: exGstCost,
    productCode: product.sku || '',
    quantity: 1,
    note: '',
  };
};

/**
 * Product-code field with catalog autocomplete, mirroring the ticket
 * line-item product box (Type-C SearchAhead). The field shows the SKU;
 * typing filters the product catalog by code OR description; picking a
 * product commits its SKU; a free-typed code commits on blur (ad-hoc codes
 * are allowed). Locked quotes fall back to a read-only text field.
 */
const ProductCodeAutocomplete: React.FC<{
  value: string;
  products: CatalogProduct[];
  disabled?: boolean;
  onChange: (code: string) => void;
}> = ({ value, products, disabled, onChange }) => {
  const [query, setQuery] = useState(value);
  // Re-sync when the stored code changes externally (e.g. a picker fills it).
  useEffect(() => setQuery(value), [value]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q
      ? products.filter(
          (p) =>
            p.sku.toLowerCase().includes(q) ||
            (p.displayName?.toLowerCase().includes(q) ?? false),
        )
      : products;
    // Cap the list so a huge catalog doesn't render thousands of rows.
    return base
      .slice(0, 50)
      .map((p) => ({ code: p.sku, description: p.displayName ?? '' }));
  }, [products, query]);

  if (disabled) {
    return (
      <TextField
        label="Product code"
        value={value}
        size="small"
        disabled
        sx={{ width: 200 }}
      />
    );
  }

  return (
    <div className="desktop-build__product-code" style={{ width: 200 }}>
      <SearchAhead
        label="Product code"
        placeholder="Product code"
        value={query}
        results={results}
        onSearch={setQuery}
        onSelect={(r) => {
          setQuery(r.code);
          onChange(r.code);
        }}
        onBlur={(text) => {
          const next = text.trim();
          if (next !== value) onChange(next);
        }}
      />
    </div>
  );
};

interface DesktopBuildEditorProps {
  items: QuoteItem[];
  onChange: (items: QuoteItem[]) => void;
  disabled?: boolean;
  /** Quote-level GST display mode — forwarded to the picker + price fields. */
  priceDisplayMode?: 'ex' | 'inc';
  /** Quote-level markup applied by the supplier picker. */
  markup?: number;
}

/**
 * Build-sheet editor for New Desktop (Custom) quotes.
 *
 * Presents the fixed component categories (CPU, Motherboard, GPU, RAM, …) as a
 * scaffold of cards. Each PART is stored as one QuoteItem carrying a single
 * selected option (always `markedForInvoice`), grouped by the item's
 * `category` slug — so multi-part categories (GPU / RAM / Case Fans / Hard
 * Drive) hold several parts, all INCLUDED in the build (not alternatives). The
 * whole build always promotes to one consolidated summary line, so there is no
 * "options / pick-one" affordance here. Every part carries an internal note
 * that never reaches the customer preview or the invoice.
 *
 * Parts are sourced through the same disti / retail / inventory / past-option
 * pickers as the standard QuoteEditor.
 */
export const DesktopBuildEditor: React.FC<DesktopBuildEditorProps> = ({
  items,
  onChange,
  disabled,
  priceDisplayMode = 'ex',
  markup = 0.115,
}) => {
  const gstSuffix = priceDisplayMode === 'inc' ? 'inc-GST' : 'ex-GST';
  const buildProfit = useMemo(
    () => computeDesktopBuildProfit(items, priceDisplayMode),
    [items, priceDisplayMode],
  );

  // Product catalog for the per-part product-code autocomplete (shared cache).
  const { products } = useProductCatalog();

  // Picker targets — each holds the category slug the pick lands in.
  const [supplierTarget, setSupplierTarget] = useState<string | null>(null);
  const [retailTarget, setRetailTarget] = useState<string | null>(null);
  const [inventoryTarget, setInventoryTarget] = useState<string | null>(null);
  const [pastTarget, setPastTarget] = useState<string | null>(null);

  // The scaffold is DERIVED from `items`: every category (even empty) is
  // stored as a placeholder QuoteItem, so removals / renames / custom
  // categories persist across a reload. Only view state stays local.
  const [newCatLabel, setNewCatLabel] = useState('');
  // Collapsed category cards — purely cosmetic scratch state, keyed by slug.
  const [collapsedSlugs, setCollapsedSlugs] = useState<string[]>([]);
  const seededRef = useRef(false);

  const toggleCollapsed = useCallback((slug: string) => {
    setCollapsedSlugs((s) =>
      s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug],
    );
  }, []);

  // Materialise the scaffold once. A build is "initialised" once it holds any
  // placeholder (an empty category item). A brand-new or legacy build (no
  // placeholders) gets a placeholder for every catalog category not already
  // present as a part — so the full scaffold shows AND each category persists.
  // An already-initialised build is left untouched, so removed categories stay
  // removed.
  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;
    const hasPlaceholder = items.some(
      (it) => it.category && it.options.length === 0,
    );
    if (hasPlaceholder) return;
    const present = new Set(
      items.map((it) => it.category).filter(Boolean) as string[],
    );
    const toAdd = DESKTOP_BUILD_CATEGORIES.filter((c) => !present.has(c.slug));
    if (toAdd.length === 0) return;
    onChange([
      ...items,
      ...toAdd.map((c, i) =>
        categoryPlaceholder(c.slug, c.label, items.length + i),
      ),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const slugPresent = useCallback(
    (slug: string): boolean =>
      items.some((it) => (it.category || 'other') === slug),
    [items],
  );

  // Parts in a category = its items that carry an option (placeholders excluded).
  const partsFor = useCallback(
    (slug: string): QuoteItem[] =>
      items
        .filter(
          (it) => (it.category || 'other') === slug && it.options.length > 0,
        )
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    [items],
  );

  // The label lives on the category's items (placeholder + parts) so it persists.
  const effectiveLabel = useCallback(
    (slug: string): string => {
      const named = items.find(
        (it) => (it.category || 'other') === slug && (it.name || '').trim(),
      );
      return (named?.name || '').trim() || desktopBuildCategoryLabel(slug);
    },
    [items],
  );

  const updateOption = useCallback(
    (itemId: string, patch: Partial<QuoteOption>) => {
      onChange(
        items.map((it) =>
          it.id === itemId
            ? { ...it, options: it.options.map((o) => ({ ...o, ...patch })) }
            : it,
        ),
      );
    },
    [items, onChange],
  );

  const removePart = useCallback(
    (itemId: string) => {
      onChange(
        items
          .filter((it) => it.id !== itemId)
          .map((it, idx) => ({ ...it, position: idx })),
      );
    },
    [items, onChange],
  );

  const addPart = useCallback(
    (slug: string, option: QuoteOption) => {
      const withId: QuoteOption =
        option.id && option.id.length > 0 ? option : { ...option, id: newId() };
      const item: QuoteItem = {
        id: newId(),
        name: effectiveLabel(slug),
        category: slug,
        position: items.length,
        selectedOptionId: withId.id,
        markedForInvoice: true,
        options: [withId],
      };
      onChange([...items, item]);
    },
    [items, onChange, effectiveLabel],
  );

  // Rename rides onto every item in the category (placeholder + parts) so it
  // persists and drives the summary label.
  const setCategoryLabel = useCallback(
    (slug: string, label: string) => {
      onChange(
        items.map((it) =>
          (it.category || 'other') === slug ? { ...it, name: label } : it,
        ),
      );
    },
    [items, onChange],
  );

  const removeCategory = useCallback(
    (slug: string) => {
      const parts = partsFor(slug);
      if (
        parts.length > 0 &&
        !window.confirm(
          `Remove "${effectiveLabel(slug)}" and its ${parts.length} part${
            parts.length === 1 ? '' : 's'
          } from this build?`,
        )
      ) {
        return;
      }
      onChange(
        items
          .filter((it) => (it.category || 'other') !== slug)
          .map((it, idx) => ({ ...it, position: idx })),
      );
    },
    [items, onChange, partsFor, effectiveLabel],
  );

  const addCategory = useCallback(
    (slug: string, label: string) => {
      if (slugPresent(slug)) return;
      onChange([...items, categoryPlaceholder(slug, label, items.length)]);
    },
    [items, onChange, slugPresent],
  );

  const addCustomCategory = useCallback(() => {
    const label = newCatLabel.trim();
    if (!label) return;
    addCategory(`${CUSTOM_CATEGORY_PREFIX}_${newId().slice(0, 8)}`, label);
    setNewCatLabel('');
  }, [newCatLabel, addCategory]);

  // Categories to render: present catalog categories (catalog order), then
  // present custom categories (first-seen by position). "Present" = the build
  // has at least one item (placeholder or part) with that slug.
  const presentCatalog = useMemo(
    () => DESKTOP_BUILD_CATEGORIES.filter((c) => slugPresent(c.slug)),
    [slugPresent],
  );
  const customSlugs = useMemo(() => {
    const seen: string[] = [];
    for (const it of [...items].sort(
      (a, b) => (a.position ?? 0) - (b.position ?? 0),
    )) {
      const c = it.category || '';
      if (c && isCustomCategorySlug(c) && !seen.includes(c)) seen.push(c);
    }
    return seen;
  }, [items]);
  const availableCatalog = useMemo(
    () => DESKTOP_BUILD_CATEGORIES.filter((c) => !slugPresent(c.slug)),
    [slugPresent],
  );

  const renderPartCard = (slug: string, item: QuoteItem) => {
    const opt = item.options[0];
    if (!opt) return null;
    return (
      <Paper key={item.id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <TextField
            label="Part description"
            value={opt.description}
            size="small"
            onChange={(e) =>
              updateOption(item.id, { description: e.target.value })
            }
            disabled={disabled}
            fullWidth
          />
          <ProductCodeAutocomplete
            value={opt.productCode}
            products={products}
            disabled={disabled}
            onChange={(code) => updateOption(item.id, { productCode: code })}
          />
          <BillingGroupSelect
            item={item}
            items={items}
            onChange={onChange}
            disabled={disabled}
            sx={{ width: 150 }}
          />
          <Tooltip title="Remove part">
            <span>
              <IconButton
                size="small"
                onClick={() => removePart(item.id)}
                disabled={disabled}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <PriceInput
            label={`Buy ${gstSuffix}`}
            exValue={opt.buyPriceExGst}
            priceDisplayMode={priceDisplayMode}
            onChange={(exVal) => updateOption(item.id, { buyPriceExGst: exVal })}
            disabled={disabled}
            sx={{ width: 130 }}
          />
          <PriceInput
            label={`Shipping ${gstSuffix}`}
            exValue={opt.shipping}
            priceDisplayMode={priceDisplayMode}
            onChange={(exVal) => updateOption(item.id, { shipping: exVal })}
            disabled={disabled}
            sx={{ width: 130 }}
          />
          <TextField
            label="Qty"
            type="text"
            inputMode="numeric"
            size="small"
            value={String(optionQty(opt))}
            onChange={(e) => {
              const parsed = parseInt(e.target.value.trim(), 10);
              updateOption(item.id, {
                quantity: Number.isFinite(parsed) && parsed >= 1 ? parsed : 1,
              });
            }}
            disabled={disabled}
            sx={{ width: 72 }}
          />
          <PriceInput
            label={`Sell/unit ${gstSuffix}`}
            exValue={opt.sellPrice}
            priceDisplayMode={priceDisplayMode}
            onChange={(exVal) => updateOption(item.id, { sellPrice: exVal })}
            disabled={disabled}
            sx={{ width: 130 }}
          />
          <TextField
            label="Source URL"
            size="small"
            value={opt.url}
            onChange={(e) => updateOption(item.id, { url: e.target.value })}
            onBlur={(e) => {
              const cleaned = cleanSupplierUrl(e.target.value);
              if (cleaned !== e.target.value) {
                updateOption(item.id, { url: cleaned });
              }
            }}
            disabled={disabled}
            sx={{ flex: 1 }}
          />
        </Stack>
        <TextField
          label="Internal note"
          placeholder="Not shown to the customer or on the invoice"
          value={opt.note ?? ''}
          size="small"
          onChange={(e) => updateOption(item.id, { note: e.target.value })}
          disabled={disabled}
          fullWidth
          multiline
          minRows={1}
          maxRows={4}
        />
        {optionQty(opt) > 1 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.5 }}
          >
            Line total {gstSuffix}: $
            {optionLineSellDisplay(opt, priceDisplayMode).toFixed(2)} (
            {optionQty(opt)} × {opt.sellPrice.toFixed(2)})
          </Typography>
        )}
        {opt.url && (
          <Link
            href={opt.url}
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
            sx={{ display: 'block', mt: 0.5 }}
          >
            Open supplier link
          </Link>
        )}
        {specDiffersFromDescription(opt.sourceSpecs, opt.description) && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.5 }}
          >
            Specs: {formatSpecSummary(opt.sourceSpecs)}
          </Typography>
        )}
      </Paper>
    );
  };

  const renderCategory = (slug: string, allowMultiple: boolean) => {
    const parts = partsFor(slug);
    const showAdd = allowMultiple || parts.length === 0;
    const addVerb = parts.length === 0 ? 'Add' : 'Add another';
    const collapsed = collapsedSlugs.includes(slug);
    // One-line preview of the parts inside, shown next to the label when the
    // card is collapsed. Quantity-aware, joined with " + " like the summary.
    const collapsedSummary = parts
      .map((it) => {
        const o = it.options[0];
        const desc = (o?.description || '').trim();
        if (!desc) return '';
        const qty = o ? optionQty(o) : 1;
        return qty > 1 ? `${qty}× ${desc}` : desc;
      })
      .filter(Boolean)
      .join(' + ');
    return (
      <Paper
        key={slug}
        variant="outlined"
        sx={{ p: 2, mb: 1.5, borderRadius: '8px' }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ mb: collapsed || !(parts.length || showAdd) ? 0 : 1.5 }}
        >
          <Tooltip title={collapsed ? 'Expand' : 'Collapse'}>
            <IconButton
              size="small"
              onClick={() => toggleCollapsed(slug)}
              sx={{ ml: -0.5 }}
            >
              <ExpandMoreIcon
                fontSize="small"
                sx={{
                  transition: 'transform 0.15s',
                  transform: collapsed ? 'rotate(-90deg)' : 'none',
                }}
              />
            </IconButton>
          </Tooltip>
          <TextField
            variant="standard"
            value={effectiveLabel(slug)}
            onChange={(e) => setCategoryLabel(slug, e.target.value)}
            disabled={disabled}
            InputProps={{
              disableUnderline: true,
              sx: { fontSize: 14, fontWeight: 600, color: '#1a1a1a' },
            }}
            sx={{ flex: collapsed ? '0 1 auto' : 1 }}
          />
          {collapsed && collapsedSummary && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              title={collapsedSummary}
              sx={{ flex: 1, minWidth: 0, textAlign: 'right' }}
            >
              {collapsedSummary}
            </Typography>
          )}
          {allowMultiple && (
            <Chip
              label="multiple"
              size="small"
              sx={{
                height: 20,
                fontSize: 11,
                bgcolor: '#f0f3f7',
                color: '#666',
              }}
            />
          )}
          <Tooltip title="Remove category">
            <span>
              <IconButton
                size="small"
                onClick={() => removeCategory(slug)}
                disabled={disabled}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        <Collapse in={!collapsed} unmountOnExit>
          {parts.map((item) => renderPartCard(slug, item))}

          {parts.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: 13, mb: showAdd ? 1 : 0 }}
            >
              No {effectiveLabel(slug).toLowerCase()} added yet.
            </Typography>
          )}

          {showAdd && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              startIcon={<StorefrontIcon />}
              onClick={() => setSupplierTarget(slug)}
              disabled={disabled}
            >
              {addVerb} — Find Product
            </Button>
            <Button
              size="small"
              startIcon={<LinkIcon />}
              onClick={() => setRetailTarget(slug)}
              disabled={disabled}
            >
              Retail URL
            </Button>
            <Button
              size="small"
              startIcon={<InventoryIcon />}
              onClick={() => setInventoryTarget(slug)}
              disabled={disabled}
            >
              Inventory
            </Button>
            <Button
              size="small"
              startIcon={<SearchIcon />}
              onClick={() => setPastTarget(slug)}
              disabled={disabled}
            >
              Past parts
            </Button>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() =>
                addPart(slug, {
                  ...blankOption(),
                  // Hand-added parts seed the category's default product code;
                  // picker-sourced parts (above) keep their own code.
                  productCode: desktopBuildDefaultProductCode(slug),
                })
              }
              disabled={disabled}
            >
              Blank
            </Button>
            </Stack>
          )}
        </Collapse>
      </Paper>
    );
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Build sheet — request a part for each component. Categories marked{' '}
        <em>multiple</em> can hold several parts (all included in the build).
        The whole build is quoted and invoiced as a single summary line; the
        per-part notes stay internal.
      </Alert>

      <DesktopBuildProfitSummary profit={buildProfit} gstSuffix={gstSuffix} />

      {presentCatalog.map((c) => renderCategory(c.slug, c.allowMultiple))}

      {customSlugs.map((slug) =>
        renderCategory(slug, desktopBuildCategoryAllowsMultiple(slug)),
      )}

      <BillingGroupsPanel
        items={items}
        onChange={onChange}
        disabled={disabled}
        priceDisplayMode={priceDisplayMode}
      />

      <Divider sx={{ my: 2 }} />

      {availableCatalog.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ mb: 1.5 }}
          flexWrap="wrap"
          useFlexGap
        >
          <Typography variant="caption" color="text.secondary">
            Add a category:
          </Typography>
          {availableCatalog.map((c) => (
            <Button
              key={c.slug}
              size="small"
              variant="text"
              startIcon={<AddIcon />}
              onClick={() => addCategory(c.slug, c.label)}
              disabled={disabled}
            >
              {c.label}
            </Button>
          ))}
        </Stack>
      )}

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          placeholder="Add a custom category (e.g. Capture Card)"
          value={newCatLabel}
          onChange={(e) => setNewCatLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addCustomCategory();
            }
          }}
          disabled={disabled}
          sx={{ width: 320 }}
        />
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addCustomCategory}
          disabled={disabled || !newCatLabel.trim()}
        >
          Add custom category
        </Button>
      </Stack>

      <InventoryPickerDialog
        open={inventoryTarget !== null}
        onClose={() => setInventoryTarget(null)}
        quoteType="new_desktop_custom"
        onPick={(product) => {
          if (!inventoryTarget) return;
          addPart(inventoryTarget, optionFromInventory(product));
          setInventoryTarget(null);
        }}
      />

      <QuoteOptionSearchDialog
        open={pastTarget !== null}
        onClose={() => setPastTarget(null)}
        onPick={(result) => {
          if (!pastTarget) return;
          addPart(pastTarget, {
            id: newId(),
            description: result.description,
            buyPriceExGst: result.buyPriceExGst,
            shipping: result.shipping,
            url: result.url,
            sellPrice: result.sellPrice,
            productCode: result.productCode,
            quantity: 1,
            note: '',
          });
          setPastTarget(null);
        }}
      />

      <DistiListingSearchDialog
        open={supplierTarget !== null}
        onClose={() => setSupplierTarget(null)}
        priceDisplayMode={priceDisplayMode}
        markup={markup}
        onPick={(result) => {
          if (!supplierTarget) return;
          addPart(supplierTarget, {
            id: newId(),
            description: result.description,
            buyPriceExGst: result.costExGst,
            shipping: result.shippingExGst,
            url: result.distiUrl,
            sellPrice: result.priceUnknown ? 0 : result.sellPriceExGst,
            productCode: result.productCode || '',
            imageUrl: result.imageUrl,
            sourceDistiId: result.distiId,
            sourceDistiSku: result.distiSku,
            sourceSpecs: result.specs,
            quantity: 1,
            note: '',
          });
          // Single-slot categories close after a pick to avoid accidental
          // duplicates; multi categories keep the dialog open so the tech can
          // add several parts (e.g. multiple RAM kits) in one sitting.
          if (!desktopBuildCategoryAllowsMultiple(supplierTarget)) {
            setSupplierTarget(null);
          }
        }}
      />

      <AddRetailOptionDialog
        open={retailTarget !== null}
        onClose={() => setRetailTarget(null)}
        onPick={(preview, overrides, commit) => {
          if (!retailTarget) return;
          addPart(retailTarget, {
            id: newId(),
            description: overrides.description,
            buyPriceExGst: overrides.buyPriceExGst,
            shipping: 0,
            url: preview.url,
            sellPrice: overrides.sellPriceExGst,
            productCode: overrides.productCode,
            deviceType: overrides.deviceType,
            imageUrl: preview.imageUrl ?? null,
            sourceSpecs: preview.specs,
            sourceDistiId: commit.distiId,
            sourceDistiSku: commit.distiSku,
            quantity: 1,
            note: '',
          });
          setRetailTarget(null);
        }}
      />
    </Box>
  );
};
